import React, { useState, useEffect, useRef } from 'react';
import FloatingDock from '@/components/layout/FloatingDock';
import Footer from '@/components/layout/Footer';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Play, Users, TrendingUp, Circle, Upload, X, Film, Radio, MessageCircle, Heart, Send } from 'lucide-react';
import { motion } from 'framer-motion';
import axios from 'axios';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';
const WS_URL = process.env.REACT_APP_BACKEND_URL?.replace('https://', 'wss://').replace('http://', 'ws://') + '/api';

const Videos = ({ user }) => {
  const [activeTab, setActiveTab] = useState('trending');
  const [trendingVideos, setTrendingVideos] = useState([]);
  const [liveStreams, setLiveStreams] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Video upload state
  const [videoFile, setVideoFile] = useState(null);
  const [thumbnailFile, setThumbnailFile] = useState(null);
  const [videoContent, setVideoContent] = useState('');
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const videoInputRef = useRef(null);
  const thumbnailInputRef = useRef(null);
  
  // Live stream state
  const [activeStream, setActiveStream] = useState(null);
  const [streamChat, setStreamChat] = useState([]);
  const [chatMessage, setChatMessage] = useState('');
  const [ws, setWs] = useState(null);

  useEffect(() => {
    if (activeTab === 'trending') {
      fetchTrendingVideos();
    } else if (activeTab === 'live') {
      fetchLiveStreams();
    }
  }, [activeTab]);

  // Cleanup WebSocket on unmount
  useEffect(() => {
    return () => {
      if (ws) {
        ws.close();
      }
    };
  }, [ws]);

  const fetchTrendingVideos = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/video/posts/trending`, {
        withCredentials: true,
      });
      setTrendingVideos(response.data);
    } catch (error) {
      toast.error('Failed to load videos');
    } finally {
      setLoading(false);
    }
  };

  const fetchLiveStreams = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/video/live/active`, {
        withCredentials: true,
      });
      setLiveStreams(response.data);
    } catch (error) {
      toast.error('Failed to load live streams');
    } finally {
      setLoading(false);
    }
  };

  const handleVideoSelect = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      if (file.size > 100 * 1024 * 1024) {
        toast.error('Video must be under 100MB');
        return;
      }
      const validTypes = ['video/mp4', 'video/webm', 'video/quicktime'];
      if (!validTypes.includes(file.type)) {
        toast.error('Please select a valid video file (MP4, WebM, MOV)');
        return;
      }
      setVideoFile(file);
    }
  };

  const handleThumbnailSelect = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        toast.error('Thumbnail must be under 5MB');
        return;
      }
      setThumbnailFile(file);
    }
  };

  const uploadVideo = async () => {
    if (!videoFile) {
      toast.error('Please select a video file');
      return;
    }
    if (!videoContent.trim()) {
      toast.error('Please add a description');
      return;
    }

    setUploading(true);
    setUploadProgress(0);

    const formData = new FormData();
    formData.append('video', videoFile);
    formData.append('content', videoContent);
    if (thumbnailFile) {
      formData.append('thumbnail', thumbnailFile);
    }

    try {
      await axios.post(`${API_URL}/video-upload/upload`, formData, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' },
        onUploadProgress: (progressEvent) => {
          const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          setUploadProgress(percentCompleted);
        }
      });
      toast.success('Video uploaded successfully!');
      setVideoFile(null);
      setThumbnailFile(null);
      setVideoContent('');
      setUploadProgress(0);
      fetchTrendingVideos();
      setActiveTab('trending');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload video');
    } finally {
      setUploading(false);
    }
  };

  const startLiveStream = async () => {
    try {
      const response = await axios.post(
        `${API_URL}/video/live/start`,
        {
          title: 'My Live Stream',
          description: 'Live streaming now!',
        },
        { withCredentials: true }
      );
      toast.success('Live stream started!');
      toast.info(`Stream Key: ${response.data.stream_key}`, { duration: 10000 });
      fetchLiveStreams();
    } catch (error) {
      toast.error('Failed to start stream');
    }
  };

  const joinStream = async (stream) => {
    setActiveStream(stream);
    
    // Fetch chat history
    try {
      const response = await axios.get(`${API_URL}/livestream/chat/${stream.stream_id}`, {
        withCredentials: true
      });
      setStreamChat(response.data.messages || []);
    } catch (error) {
      console.error('Failed to load chat history');
    }

    // Connect to WebSocket
    const wsUrl = `${WS_URL}/livestream/ws/${stream.stream_id}`;
    const socket = new WebSocket(wsUrl);
    
    socket.onopen = () => {
      toast.success('Connected to stream');
    };
    
    socket.onmessage = (event) => {
      const data = JSON.parse(event.data);
      if (data.type === 'chat') {
        setStreamChat(prev => [...prev, data]);
      } else if (data.type === 'viewer_update') {
        setActiveStream(prev => prev ? {...prev, viewers_count: data.viewers_count} : null);
      } else if (data.type === 'reaction') {
        // Could show floating reactions here
      }
    };
    
    socket.onclose = () => {
      toast.info('Disconnected from stream');
    };
    
    setWs(socket);
  };

  const leaveStream = () => {
    if (ws) {
      ws.close();
      setWs(null);
    }
    setActiveStream(null);
    setStreamChat([]);
  };

  const sendChatMessage = () => {
    if (!chatMessage.trim() || !ws) return;
    
    ws.send(JSON.stringify({
      type: 'chat',
      user_id: user?.user_id,
      username: user?.username,
      avatar: user?.avatar,
      content: chatMessage
    }));
    
    setChatMessage('');
  };

  const sendReaction = () => {
    if (!ws) return;
    ws.send(JSON.stringify({
      type: 'reaction',
      reaction: '❤️',
      user_id: user?.user_id
    }));
  };

  // Live Stream Viewer Component
  if (activeStream) {
    return (
      <div className="min-h-screen bg-black" data-testid="stream-viewer">
        <div className="flex h-screen">
          {/* Video Area */}
          <div className="flex-1 relative">
            <div className="absolute inset-0 bg-gradient-to-br from-purple-900 to-pink-900 flex items-center justify-center">
              <div className="text-center text-white">
                <Radio className="w-20 h-20 mb-4 animate-pulse" />
                <h2 className="text-2xl font-bold mb-2">{activeStream.title}</h2>
                <p className="text-white/70">{activeStream.description}</p>
                <div className="flex items-center justify-center gap-2 mt-4">
                  <Users className="w-5 h-5" />
                  <span>{activeStream.viewers_count} watching</span>
                </div>
              </div>
            </div>
            
            {/* Controls */}
            <div className="absolute top-4 left-4 flex items-center gap-2">
              <div className="bg-red-500 text-white px-3 py-1 rounded-full text-sm font-bold flex items-center gap-1">
                <Circle className="w-2 h-2 animate-pulse" fill="white" />
                LIVE
              </div>
            </div>
            
            <Button
              onClick={leaveStream}
              className="absolute top-4 right-4 rounded-full bg-white/20 hover:bg-white/30"
              size="icon"
              data-testid="leave-stream-btn"
            >
              <X className="w-5 h-5 text-white" />
            </Button>
            
            {/* Reaction Button */}
            <Button
              onClick={sendReaction}
              className="absolute bottom-4 left-4 rounded-full bg-pink-500 hover:bg-pink-600"
              size="icon"
              data-testid="reaction-btn"
            >
              <Heart className="w-5 h-5 text-white" fill="white" />
            </Button>
          </div>
          
          {/* Chat Sidebar */}
          <div className="w-80 bg-gray-900 flex flex-col">
            <div className="p-4 border-b border-gray-800">
              <h3 className="text-white font-bold flex items-center gap-2">
                <MessageCircle className="w-5 h-5" />
                Live Chat
              </h3>
            </div>
            
            {/* Chat Messages */}
            <div className="flex-1 overflow-y-auto p-4 space-y-3">
              {streamChat.map((msg, idx) => (
                <div key={msg.message_id || idx} className="flex gap-2">
                  <div className="w-8 h-8 rounded-full bg-gradient-to-br from-purple-500 to-pink-500 flex-shrink-0 flex items-center justify-center text-white text-xs font-bold">
                    {msg.username?.[0]?.toUpperCase() || '?'}
                  </div>
                  <div>
                    <span className="text-purple-400 text-sm font-semibold">@{msg.username}</span>
                    <p className="text-white text-sm">{msg.content}</p>
                  </div>
                </div>
              ))}
            </div>
            
            {/* Chat Input */}
            <div className="p-4 border-t border-gray-800">
              <div className="flex gap-2">
                <Input
                  value={chatMessage}
                  onChange={(e) => setChatMessage(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && sendChatMessage()}
                  placeholder="Say something..."
                  className="bg-gray-800 border-gray-700 text-white placeholder-gray-500 rounded-full"
                  data-testid="chat-input"
                />
                <Button
                  onClick={sendChatMessage}
                  className="rounded-full bg-purple-500 hover:bg-purple-600"
                  size="icon"
                  data-testid="send-chat-btn"
                >
                  <Send className="w-4 h-4" />
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-50 via-accent-50 to-secondary-50 pb-32" data-testid="videos-page">
      <div className="max-w-6xl mx-auto px-6 pt-8">
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-6"
        >
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-red-500 to-pink-500 flex items-center justify-center">
                <Play className="w-7 h-7 text-white" />
              </div>
              <h1 className="text-4xl font-heading font-black text-gray-900" data-testid="videos-title">
                Videos
              </h1>
            </div>
            <Button
              onClick={startLiveStream}
              className="rounded-full bg-red-500 hover:bg-red-600 text-white font-bold px-6 shadow-lg"
              data-testid="go-live-btn"
            >
              <Radio className="w-4 h-4 mr-2" />
              Go Live
            </Button>
          </div>
        </motion.div>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="grid w-full grid-cols-3 rounded-2xl p-1 bg-white/70 backdrop-blur-md mb-6">
            <TabsTrigger value="trending" className="rounded-xl font-bold" data-testid="trending-tab">
              <TrendingUp className="w-4 h-4 mr-2" />
              Trending
            </TabsTrigger>
            <TabsTrigger value="live" className="rounded-xl font-bold" data-testid="live-tab">
              <Radio className="w-4 h-4 mr-2" />
              Live
            </TabsTrigger>
            <TabsTrigger value="upload" className="rounded-xl font-bold" data-testid="upload-tab">
              <Upload className="w-4 h-4 mr-2" />
              Upload
            </TabsTrigger>
          </TabsList>

          <TabsContent value="trending">
            {loading ? (
              <div className="flex justify-center py-12">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
              </div>
            ) : trendingVideos.length === 0 ? (
              <Card className="p-12 rounded-3xl border border-white/20 shadow-xl bg-white/70 backdrop-blur-md text-center">
                <Film className="w-16 h-16 mx-auto mb-4 text-gray-400" />
                <p className="text-lg text-gray-600">No videos yet. Be the first to upload!</p>
                <Button 
                  onClick={() => setActiveTab('upload')} 
                  className="mt-4 rounded-full bg-primary"
                  data-testid="upload-first-video-btn"
                >
                  Upload Video
                </Button>
              </Card>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {trendingVideos.map((video, idx) => (
                  <motion.div
                    key={video.post_id}
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ delay: idx * 0.1 }}
                  >
                    <Card className="rounded-3xl overflow-hidden border border-white/20 shadow-xl bg-white/70 backdrop-blur-md hover:shadow-2xl hover:-translate-y-2 transition-all cursor-pointer" data-testid={`video-${idx}`}>
                      <div className="aspect-video bg-gradient-to-br from-primary-100 to-accent-100 flex items-center justify-center relative">
                        {video.thumbnail_url ? (
                          <img src={`${process.env.REACT_APP_BACKEND_URL}${video.thumbnail_url}`} alt="Thumbnail" className="w-full h-full object-cover" />
                        ) : (
                          <Play className="w-16 h-16 text-white/70" />
                        )}
                        <div className="absolute inset-0 bg-black/20 flex items-center justify-center opacity-0 hover:opacity-100 transition-opacity">
                          <Play className="w-16 h-16 text-white" fill="white" />
                        </div>
                      </div>
                      <div className="p-4">
                        <p className="text-gray-800 font-semibold mb-2 line-clamp-2">{video.content}</p>
                        <div className="flex items-center gap-2 text-sm text-gray-600">
                          <span>{video.views_count} views</span>
                          <span>•</span>
                          <span>{video.likes_count} likes</span>
                        </div>
                        {video.user && (
                          <p className="text-sm text-gray-500 mt-2">@{video.user.username}</p>
                        )}
                      </div>
                    </Card>
                  </motion.div>
                ))}
              </div>
            )}
          </TabsContent>

          <TabsContent value="live">
            {loading ? (
              <div className="flex justify-center py-12">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
              </div>
            ) : liveStreams.length === 0 ? (
              <Card className="p-12 rounded-3xl border border-white/20 shadow-xl bg-white/70 backdrop-blur-md text-center">
                <Radio className="w-16 h-16 mx-auto mb-4 text-gray-400" />
                <p className="text-lg text-gray-600">No live streams right now</p>
                <Button 
                  onClick={startLiveStream} 
                  className="mt-4 rounded-full bg-red-500 hover:bg-red-600"
                  data-testid="start-streaming-btn"
                >
                  <Radio className="w-4 h-4 mr-2" />
                  Start Streaming
                </Button>
              </Card>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {liveStreams.map((stream, idx) => (
                  <motion.div
                    key={stream.stream_id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: idx * 0.1 }}
                  >
                    <Card 
                      className="rounded-3xl overflow-hidden border border-white/20 shadow-xl bg-white/70 backdrop-blur-md hover:shadow-2xl transition-all cursor-pointer" 
                      onClick={() => joinStream(stream)}
                      data-testid={`stream-${idx}`}
                    >
                      <div className="aspect-video bg-gradient-to-br from-red-500 to-pink-500 flex items-center justify-center relative">
                        <Radio className="w-20 h-20 text-white animate-pulse" />
                        <div className="absolute top-4 left-4 bg-red-500 text-white px-3 py-1 rounded-full text-xs font-bold flex items-center gap-1">
                          <Circle className="w-2 h-2 animate-pulse" fill="white" />
                          LIVE
                        </div>
                      </div>
                      <div className="p-4">
                        <h3 className="font-bold text-lg text-gray-900 mb-1">{stream.title}</h3>
                        <p className="text-sm text-gray-600 mb-2">{stream.description}</p>
                        <div className="flex items-center gap-2 text-sm text-gray-600">
                          <Users className="w-4 h-4" />
                          <span>{stream.viewers_count} watching</span>
                        </div>
                        {stream.user && (
                          <p className="text-sm text-gray-500 mt-2">@{stream.user.username}</p>
                        )}
                      </div>
                    </Card>
                  </motion.div>
                ))}
              </div>
            )}
          </TabsContent>

          <TabsContent value="upload">
            <Card className="p-8 rounded-3xl border border-white/20 shadow-xl bg-white/70 backdrop-blur-md">
              <h3 className="text-2xl font-heading font-bold mb-6 text-gray-900 flex items-center gap-2">
                <Upload className="w-6 h-6" />
                Upload a Video
              </h3>
              
              <div className="space-y-6">
                {/* Video File Selection */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Video File</label>
                  <input
                    ref={videoInputRef}
                    type="file"
                    accept="video/mp4,video/webm,video/quicktime"
                    onChange={handleVideoSelect}
                    className="sr-only"
                  />
                  {videoFile ? (
                    <div className="flex items-center gap-3 p-4 bg-green-50 rounded-xl border-2 border-green-200">
                      <Film className="w-8 h-8 text-green-600" />
                      <div className="flex-1">
                        <p className="font-semibold text-green-800">{videoFile.name}</p>
                        <p className="text-sm text-green-600">{(videoFile.size / (1024 * 1024)).toFixed(2)} MB</p>
                      </div>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => setVideoFile(null)}
                        className="text-green-600 hover:text-green-800"
                        data-testid="remove-video-btn"
                      >
                        <X className="w-5 h-5" />
                      </Button>
                    </div>
                  ) : (
                    <div
                      onClick={() => videoInputRef.current?.click()}
                      className="border-2 border-dashed border-gray-300 rounded-xl p-8 text-center cursor-pointer hover:border-primary hover:bg-primary-50/30 transition-colors"
                      data-testid="video-dropzone"
                    >
                      <Upload className="w-12 h-12 mx-auto mb-3 text-gray-400" />
                      <p className="font-semibold text-gray-700">Click to select video</p>
                      <p className="text-sm text-gray-500 mt-1">MP4, WebM, MOV • Max 100MB</p>
                    </div>
                  )}
                </div>

                {/* Thumbnail Selection */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Thumbnail (Optional)</label>
                  <input
                    ref={thumbnailInputRef}
                    type="file"
                    accept="image/jpeg,image/png,image/webp"
                    onChange={handleThumbnailSelect}
                    className="sr-only"
                  />
                  {thumbnailFile ? (
                    <div className="flex items-center gap-3 p-4 bg-blue-50 rounded-xl border-2 border-blue-200">
                      <img 
                        src={URL.createObjectURL(thumbnailFile)} 
                        alt="Thumbnail preview" 
                        className="w-16 h-12 object-cover rounded"
                      />
                      <div className="flex-1">
                        <p className="font-semibold text-blue-800">{thumbnailFile.name}</p>
                      </div>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => setThumbnailFile(null)}
                        className="text-blue-600 hover:text-blue-800"
                        data-testid="remove-thumbnail-btn"
                      >
                        <X className="w-5 h-5" />
                      </Button>
                    </div>
                  ) : (
                    <div
                      onClick={() => thumbnailInputRef.current?.click()}
                      className="border-2 border-dashed border-gray-200 rounded-xl p-4 text-center cursor-pointer hover:border-blue-300 hover:bg-blue-50/30 transition-colors"
                      data-testid="thumbnail-dropzone"
                    >
                      <p className="text-sm text-gray-500">Click to add thumbnail • Max 5MB</p>
                    </div>
                  )}
                </div>

                {/* Description */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Description</label>
                  <Textarea
                    placeholder="Describe your video..."
                    value={videoContent}
                    onChange={(e) => setVideoContent(e.target.value)}
                    className="rounded-xl border-2 resize-none"
                    rows={4}
                    maxLength={2000}
                    data-testid="video-description-input"
                  />
                  <p className="text-xs text-gray-500 mt-1 text-right">{videoContent.length}/2000</p>
                </div>

                {/* Upload Progress */}
                {uploading && (
                  <div className="space-y-2">
                    <div className="flex justify-between text-sm">
                      <span className="font-semibold text-gray-700">Uploading...</span>
                      <span className="text-primary font-bold">{uploadProgress}%</span>
                    </div>
                    <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                      <div 
                        className="h-full bg-gradient-to-r from-primary to-accent transition-all duration-300"
                        style={{ width: `${uploadProgress}%` }}
                      />
                    </div>
                  </div>
                )}

                {/* Upload Button */}
                <Button
                  onClick={uploadVideo}
                  disabled={uploading || !videoFile}
                  className="w-full rounded-full bg-gradient-to-r from-red-500 to-pink-500 text-white font-bold h-12 shadow-lg hover:scale-105 transition-transform disabled:opacity-50 disabled:cursor-not-allowed"
                  data-testid="upload-video-btn"
                >
                  {uploading ? 'Uploading...' : 'Upload Video'}
                </Button>
              </div>
            </Card>
          </TabsContent>
        </Tabs>
      </div>

      <FloatingDock user={user} />
    </div>
  );
};

export default Videos;
